import { create } from "zustand";

import { generateUUID } from "@/utils/util";

interface Message {
  id?: string;
  message_id?: string;
  role?: string;
  content?: string;
  message_type: string;
  attachments?: {
    additional_files: string[];
  };
  data: { content: string };
  sources?: any;
  response_time?: number | null;
  isError?: boolean;
}

import { DEFAULT_ADDITIONAL_PROMPT } from "@/constants/copilot";

interface CopilotStore {
  messages: Message[];
  chatLoading: boolean;
  uuid: string;
  currentConversationId: string;
  stopMessageId: string;
  parentId: string;
  fileError: string;
  systemPrompt: string;
  newChatSystemPrompt: string;
  clearMessages: () => void;
  addMessage: (newMessage: Message) => void;
  popMessage: () => void;
  sliceMessage: () => void;
  setCurrentConversationId: (id: string) => void;
  setStopMessageId: (id: string) => void;
  setParentId: (id: string) => void;
  setStoppedByUser: (state: any) => void;
  setFileError: (error: string) => void;
  setChatLoading: (isLoading: boolean) => void;
  setUuid: (uuid: string) => void;
  setResponseTime: (message_id: string, response_time: number) => void;
  setSystemPrompt: (prompt: string) => void;
  setNewChatSystemPrompt: (prompt: string) => void;
  resetConversation: () => void;
}

const useCopilotStore = create<CopilotStore>((set) => ({
  messages: [],
  chatLoading: false,
  uuid: generateUUID(), // frontend id
  currentConversationId: "",
  stopMessageId: "",
  parentId: "",
  fileError: "",
  systemPrompt: DEFAULT_ADDITIONAL_PROMPT,
  newChatSystemPrompt: DEFAULT_ADDITIONAL_PROMPT,
  clearMessages: () => {
    set({ messages: [] });
  },
  addMessage: (newMessage: Message) =>
    set((state) => ({
      messages: [...state.messages, newMessage],
    })),
  popMessage: () => {
    set((state) => ({
      messages: state.messages.slice(0, -2),
    }));
  },
  sliceMessage: () => {
    set((state) => ({
      messages: state.messages.slice(0, -1),
    }));
  },
  setCurrentConversationId: (id: string) => {
    set({ currentConversationId: id });
  },
  setStopMessageId: (id: string) => {
    set({ stopMessageId: id });
  },
  setParentId: (id: string) => {
    set({ parentId: id });
  },
  setStoppedByUser: () => {
    set((state) => {
      const lastMsg = state.messages[state.messages.length - 1];
      return {
        messages: [
          ...state.messages.slice(0, -1),
          { ...lastMsg, stoppedByUser: true },
        ],
      };
    });
  },
  setFileError: (error: string) => {
    set({ fileError: error });
  },
  setChatLoading: (isLoading: boolean) => {
    set({ chatLoading: isLoading });
  },
  setUuid: (uuid: string) => {
    set({ uuid: uuid });
  },
  setResponseTime: (message_id: string, response_time: number) =>
    set((state) => {
      const existingMessageIndex = state.messages.findIndex(
        (message) => message.id === message_id
      );

      if (existingMessageIndex !== -1) {
        const updatedMessages = [...state.messages];
        updatedMessages[existingMessageIndex].response_time = response_time;
        return {
          messages: updatedMessages,
        };
      }
      return {
        messages: [...state.messages],
      };
    }),
  setSystemPrompt: (prompt: string) => {
    set({ systemPrompt: prompt });
  },
  setNewChatSystemPrompt: (prompt: string) => {
    set({ newChatSystemPrompt: prompt });
  },
  
  resetConversation: () => {
    set((state) => ({
      messages: [],
      chatLoading: false,
      uuid: generateUUID(),
      currentConversationId: "",
      stopMessageId: "",
      fileError: "",
      systemPrompt: state.newChatSystemPrompt,
    }));
  },
}));

export default useCopilotStore;
